/**
 * PWA Install Prompt Component
 * Shows a banner prompting users to install the app.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const getManualInstallMessage = () => {
  const ua = window.navigator.userAgent || '';

  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'To install on iPhone/iPad, tap Share and then Add to Home Screen.';
  }

  if (/android/i.test(ua)) {
    return 'To install, open the browser menu and choose Install app or Add to Home screen.';
  }

  return 'Use your browser menu to install this app.';
};

const InstallPrompt = () => {
  const deferredPromptRef = useRef(null);
  const promptTimerRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const dismissedRecently = useMemo(() => {
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    return dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_TTL_MS;
  }, []);

  useEffect(() => {
    if (isStandalone()) {
      setShowPrompt(false);
      return undefined;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;

      if (!dismissedRecently) {
        if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
        promptTimerRef.current = setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      setShowPrompt(false);
      setIsInstalling(false);
      toast.success('App installed successfully.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
    };
  }, [dismissedRecently]);

  const handleInstall = async () => {
    const promptEvent = deferredPromptRef.current;

    if (!promptEvent || typeof promptEvent.prompt !== 'function') {
      toast(getManualInstallMessage(), { icon: '📲', duration: 6000 });
      return;
    }

    try {
      setIsInstalling(true);
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;

      if (outcome === 'accepted') {
        setShowPrompt(false);
      }

      deferredPromptRef.current = null;
    } catch (error) {
      console.error('PWA install prompt failed:', error);
      toast.error('Install prompt could not be opened. Try using your browser menu.');
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone()) return null;

  return (
    <div className="install-prompt w-[90%] max-w-md">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 flex items-center gap-4">
        <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-2xl">🏥</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">Install CDCS App</p>
          <p className="text-xs text-gray-500">Quick access from your home screen</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            Later
          </button>
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="px-4 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600"
          >
            {isInstalling ? 'Opening…' : 'Install'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
